import { checkName, Control213, ControlInlineEdit } from "aic";
import { useTlfContext } from "contexts/AppSettingsContext";
import { getVariantPatchContext } from "contexts/tlf/getVariantPatchContext";
import { REMOVE_VARIANT_PATCH, RENAME_VARIANT_PATCH } from "contexts/tlf/interface";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Patch } from "rfc6902";

/**
 * Patch identifier and callbacks for selecting and editing a named patch.
 */
export interface TlfVariantPatchRowProps {
  patchId: string;
  onToggleApplied: (applied: boolean) => void;
  onActivate: () => void;
}

/**
 * Renders one patch in the separate patch ContextWindow with apply and active-edit controls.
 *
 * @param props - Patch identifier and selection callbacks
 * @param props.patchId - Catalog key used to read patch state from the TLF context
 * @param props.onToggleApplied - Callback for applying or removing the patch
 * @param props.onActivate - Callback for making this patch the active edit target
 * @returns A patch row with applied and active visual states
 */
export const TlfVariantPatchRow = ({
  patchId,
  onToggleApplied,
  onActivate,
}: TlfVariantPatchRowProps): React.ReactElement => {
  const tlfContext = useTlfContext();
  const { activeVariantPatchName, appliedVariantPatches, dispatch, readOnly } = tlfContext;
  const { currentMaster } = getVariantPatchContext(tlfContext);
  const patchCatalog = currentMaster?.allVariantPatches ?? {};
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(patchId);
  const [nameValidation, setNameValidation] = useState({ name: patchId, valid: false });
  const nameInputId = `tlf-patch-name-${encodeURIComponent(patchId)}`;
  const nameValue = isEditingName ? editName : patchId;
  const isNameValid = nameValidation.name === nameValue && nameValidation.valid;
  const patchOperations: Patch = patchCatalog[patchId] ?? [];
  const applied = appliedVariantPatches.includes(patchId);
  const active = activeVariantPatchName === patchId;
  const rowClassName = active ? "bg-primary-subtle" : applied ? "bg-secondary-subtle" : "";
  const operationTooltip =
    patchOperations.length === 0
      ? "No operations in this patch"
      : patchOperations
          .map(
            (operation) =>
              `${operation.op} ${operation.path}${"value" in operation ? `: ${JSON.stringify(operation.value)}` : ""}`,
          )
          .join("\n");

  useEffect(() => {
    const controller = new AbortController();
    void checkName(
      "",
      {
        value: nameValue.trim(),
        options: {
          existingValues: Object.keys(currentMaster?.allVariantPatches ?? {}).filter(
            (name) => name !== patchId,
          ),
        },
      },
      controller,
    ).then((result) => {
      if (!controller.signal.aborted) {
        setNameValidation({ name: nameValue, valid: result.success && result.data.valid });
      }
    });
    return () => controller.abort();
  }, [currentMaster, nameValue, patchId]);

  /**
   * Saves a changed patch name after the shared inline control commits a validated draft.
   *
   * @param name - Current input value, trimmed before updating the patch catalogue
   */
  const saveName = (name: string): void => {
    if (name !== patchId) {
      dispatch({
        operation: RENAME_VARIANT_PATCH,
        patchName: patchId,
        newPatchName: name,
        calledFrom: "TlfVariantPatchRow:renamePatch",
      });
    }
  };

  return (
    <div className={`rounded p-2 ms-2 border border-primary ${rowClassName}`}>
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div
          className="fw-semibold text-break flex-grow-1"
          style={{ minWidth: 0 }}
        >
          <ControlInlineEdit
            id={nameInputId}
            aria-label="Patch name"
            className="fw-semibold"
            style={{ backgroundColor: "transparent" }}
            value={patchId}
            setValue={saveName}
            disabled={readOnly}
            isInvalid={!isNameValid}
            spellCheck={false}
            editLabel="Edit name"
            deleteLabel="Remove patch"
            showEditButtonWhileEditing
            onDraftChange={setEditName}
            onEditChange={setIsEditingName}
            whiteSpaceFix
            onDelete={() =>
              dispatch({
                operation: REMOVE_VARIANT_PATCH,
                patchName: patchId,
                calledFrom: "TlfVariantPatchRow:removePatch",
              })
            }
          />
        </div>
        <span
          className="small text-muted text-nowrap"
          tabIndex={0}
          aria-label={`${patchId}: ${patchOperations.length} operations`}
          title={operationTooltip}
        >
          {patchOperations.length} ops
        </span>
      </div>
      <Control213
        id={`tlf-patch-apply-${encodeURIComponent(patchId)}`}
        label={`Apply ${patchId}`}
        checked={applied}
        disabled={readOnly}
        setChecked={onToggleApplied}
      />
      <Form.Check
        id={`tlf-patch-active-${encodeURIComponent(patchId)}`}
        type="radio"
        name="tlf-active-patch"
        label={`Edit ${patchId}`}
        checked={active}
        disabled={readOnly || !applied}
        onChange={onActivate}
      />
    </div>
  );
};

TlfVariantPatchRow.displayName = "TlfVariantPatchRow";
